import { useContext } from "react";
import { Alert, Container } from "react-bootstrap";
import { Navigate, useSearchParams } from "react-router";

import { SessionContext } from "@/stores/SessionContext";

/**
 * Displays a message to the user when they are not authenticated indicating that they should log in. The message
 * is centered on the page.
 */
export const Unauthenticated: React.FC = () => {
  const { session } = useContext(SessionContext);
  const [searchParams] = useSearchParams();
  const authError = parseAuthError(searchParams);

  // Navigate back to the previous page automatically when the user signs in
  if (session) {
    const returnUrl = parseAndValidatedReturnUrl(searchParams.get("returnUrl"));
    console.info("User is now authenticated, redirecting to the return URL", returnUrl);
    return <Navigate to={returnUrl} replace />;
  }

  // Show the unauthenticated message
  return (
    <Container className="mt-5">
      <h1>You are not authenticated</h1>
      {authError && (
        <Alert variant="danger" className="mt-3">
          <strong>Sign-in failed.</strong> {authError}
        </Alert>
      )}
      <p>Please log in using the Google Sign In button at the top right to continue.</p>
    </Container>
  );
};

function parseAuthError(searchParams: URLSearchParams): string | null {
  const errorDescription = searchParams.get("error_description");
  if (!errorDescription) return null;
  const errorCode = searchParams.get("error_code");
  return errorCode ? `${errorDescription} (code: ${errorCode})` : errorDescription;
}

/**
 * Allows only same-app relative return URLs from the query string and falls back to `/` for invalid input.
 */
function parseAndValidatedReturnUrl(returnUrl: string | null): string {
  // Only support relative URLs
  if (!returnUrl?.startsWith("/")) {
    console.warn("Ignoring invalid return URL and falling back to /", returnUrl);
    return "/";
  }

  try {
    const parsedUrl = new URL(returnUrl, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) {
      console.warn("Ignoring cross-origin return URL and falling back to /", returnUrl);
      return "/";
    }

    return returnUrl;
  } catch {
    console.warn("Ignoring malformed return URL and falling back to /", returnUrl);
    return "/";
  }
}
